/**
 * GH-268 — Onboarding tour state machine + context hook.
 *
 * Single source of truth for tour state:
 *   idle      — never started this session, no `tourCompletedAt`
 *   running   — popovers visible, currentStep is 0..N-1
 *   completed — user finished or skipped (also derived from server `tourCompletedAt`)
 *
 * The context value is provided by `<TourProvider/>`. `useTour()` is the
 * consumer hook used by `<TourOverlay/>`, `<TourStep/>`, and the
 * Help-menu "Take the tour" entry.
 */

import { createContext, useContext } from 'react'
import type { TourStep, TourStepId } from './tour-step-config'

export type TourState = 'idle' | 'running' | 'completed'

export interface TourContextValue {
  /** High-level state machine value */
  state: TourState
  /** Index into TOUR_STEPS, or null when not running */
  currentStepIndex: number | null
  /** The active step object (null when idle/completed) */
  currentStep: TourStep | null
  /** Total number of steps in the tour */
  totalSteps: number
  /** True when on the last step — UI swaps "Next" → "Finish" */
  isLastStep: boolean
  /**
   * IDs of steps the user has already moved past during the current run.
   * Used by `<TourOverlay/>` to render persistent "visited" markers on the
   * anchors of completed steps. Reset on `start()`/`replay()`.
   */
  visitedSteps: TourStepId[]

  /** Begin the tour from step 0. Idempotent if already running. */
  start: () => void
  /** Advance one step. On the last step, transitions to `completed` and PATCHes the server. */
  next: () => void
  /** Step back one step. No-op at index 0. */
  prev: () => void
  /** End the tour early — counts as completed (PATCHes the server). */
  skip: () => void
  /** Restart from step 0 regardless of current state (used by Help menu). */
  replay: () => void
}

export const TourContext = createContext<TourContextValue | null>(null)

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) {
    throw new Error('useTour() must be used inside <TourProvider/>')
  }
  return ctx
}
